import { useMemo, useState } from 'react';
import { useStore } from '../store';

interface AggregatedLink {
  url: string;
  text: string;
  pages: string[];
}

export function ExternalLinksView() {
  const data = useStore((s) => s.data);
  const [filter, setFilter] = useState('');
  const [showAll, setShowAll] = useState(false);

  const aggregated = useMemo<AggregatedLink[]>(() => {
    if (!data) return [];
    const map = new Map<string, AggregatedLink>();
    for (const page of data.pages) {
      for (const link of page.externalLinks ?? []) {
        const existing = map.get(link.url);
        if (existing) {
          if (!existing.pages.includes(page.url)) existing.pages.push(page.url);
        } else {
          map.set(link.url, {
            url: link.url,
            text: link.text,
            pages: [page.url],
          });
        }
      }
    }
    return Array.from(map.values()).sort((a, b) => b.pages.length - a.pages.length);
  }, [data]);

  const filtered = useMemo(() => {
    const f = filter.trim().toLowerCase();
    let list = aggregated;
    if (f) {
      list = list.filter(
        (l) => l.url.toLowerCase().includes(f) || l.text.toLowerCase().includes(f),
      );
    }
    // 共通パーツ（全ページに出るリンク）を初期表示で抑制
    if (!showAll && data) {
      const total = data.pages.length;
      list = list.filter((l) => l.pages.length < total);
    }
    return list;
  }, [aggregated, filter, showAll, data]);

  if (!data) return <></>;
  const total = data.pages.length;
  const commonCount = aggregated.filter((l) => l.pages.length === total).length;

  return (
    <div className="rounded-lg border border-slate-200 bg-white">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 p-4">
        <div>
          <h2 className="text-sm font-semibold text-slate-700">
            外部リンク（{aggregated.length} 種類）
          </h2>
          <p className="mt-0.5 text-xs text-slate-500">
            全 {total} ページ中の出現頻度でソート。{commonCount} 件は全ページ共通リンクです。
          </p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-xs text-slate-600">
            <input
              type="checkbox"
              checked={showAll}
              onChange={(e) => setShowAll(e.target.checked)}
              className="rounded"
            />
            共通リンクも表示
          </label>
          <input
            type="search"
            placeholder="URL/テキストでフィルタ"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="w-56 rounded-md border border-slate-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none"
          />
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase text-slate-600">
            <tr>
              <th className="px-3 py-2 text-left font-medium">URL</th>
              <th className="px-3 py-2 text-left font-medium">テキスト</th>
              <th className="px-3 py-2 text-right font-medium">出現ページ数</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((l) => (
              <tr key={l.url} className="border-b border-slate-100 hover:bg-slate-50">
                <td className="max-w-md truncate px-3 py-2 font-mono text-xs">
                  <a
                    href={l.url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-blue-600 hover:underline"
                  >
                    {l.url}
                  </a>
                </td>
                <td className="max-w-sm truncate px-3 py-2 text-slate-700">{l.text || '-'}</td>
                <td className="px-3 py-2 text-right tabular-nums text-slate-700">
                  {l.pages.length} / {total}
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={3} className="px-3 py-8 text-center text-slate-400">
                  該当リンクなし
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
